import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip } from "@material-ui/core";
import { MoreVert, Replay } from "@material-ui/icons";
import AssignmentIcon from "@material-ui/icons/Assignment";
import EventIcon from "@material-ui/icons/Event";
import AlarmIcon from "@material-ui/icons/Alarm";
import ScheduleIcon from "@material-ui/icons/Schedule";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ResolveTicketDialog from "../ResolveTicketDialog";
import AppointmentModal from "../AppointmentModal";
import ScheduledMessagesListModal from "../ScheduledMessagesListModal";
import TicketAiControls from "../TicketAiControls";
import SalesAiModal from "../SalesAiModal";
import ConversationSummaryModal from "../ConversationSummaryModal";
import PaymentLinkModal from "../PaymentLinkModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MemoryOutlinedIcon from "@material-ui/icons/MemoryOutlined";
import SubjectIcon from "@material-ui/icons/Subject";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";

const useStyles = makeStyles(theme => ({
	// Era `flex: none` com 8px de margem em cada filho: dez controles somavam
	// mais largura que o cabeçalho tinha, e os botões finais saíam da tela.
	// Agora a faixa encolhe, o espaçamento vem de `gap` e as ações primárias
	// ficam protegidas de encolher.
	actionButtons: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		flex: "1 1 auto",
		minWidth: 0,
		marginLeft: "auto",
		marginRight: 6,
		justifyContent: "flex-end",
	},
	// Ícones secundários: são o que cede espaço primeiro. Em tela estreita a
	// faixa rola no lugar de empurrar os botões para fora.
	secondaryIcons: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.25),
		minWidth: 0,
		overflowX: "auto",
		scrollbarWidth: "none",
		"&::-webkit-scrollbar": { display: "none" },
	},
	// Resolver / Retornar / Aceitar nunca encolhem: são a razão da tela.
	primaryActions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		flex: "none",
	},
}));

const TicketActionButtons = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const [resolveOpen, setResolveOpen] = useState(false);
	const [appointmentOpen, setAppointmentOpen] = useState(false);
	const [appointmentKind, setAppointmentKind] = useState("task");
	const [scheduledListOpen, setScheduledListOpen] = useState(false);
	const [salesAiOpen, setSalesAiOpen] = useState(false);
	const [summaryOpen, setSummaryOpen] = useState(false);
	const [paymentLinkOpen, setPaymentLinkOpen] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);

	const contactName = ticket?.contact?.name || "";
	const kindDefaults = {
		task: { tooltip: "Nova tarefa", titlePrefix: "Tarefa" },
		meeting: { tooltip: "Novo agendamento", titlePrefix: "Agendamento" },
		reminder: { tooltip: "Novo lembrete", titlePrefix: "Lembrete" },
	};

	const openAppointmentModal = (kind) => {
		setAppointmentKind(kind);
		setAppointmentOpen(true);
	};

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleResolveConfirm = async ({ closeMode, closeMessage }) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: "closed",
				userId: user?.id || null,
				closeMode,
				closeMessage,
			});
			setResolveOpen(false);
			history.push("/tickets");
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className={classes.actionButtons}>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					<div className={classes.secondaryIcons}>
					<Tooltip title="Resumir a conversa">
						<IconButton size="small" onClick={() => setSummaryOpen(true)}>
							<SubjectIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<TicketAiControls ticket={ticket} />
					<Tooltip title="Nova tarefa">
						<IconButton size="small" onClick={() => openAppointmentModal("task")}>
							<AssignmentIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Novo agendamento">
						<IconButton size="small" onClick={() => openAppointmentModal("meeting")}>
							<EventIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Novo lembrete">
						<IconButton size="small" onClick={() => openAppointmentModal("reminder")}>
							<AlarmIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Mensagens agendadas deste cliente">
						<IconButton size="small" onClick={() => setScheduledListOpen(true)}>
							<ScheduleIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="IA de Vendas — gerar sugestao de resposta">
						<IconButton size="small" onClick={() => setSalesAiOpen(true)}>
							<MemoryOutlinedIcon fontSize="small" style={{ color: "#6a1b9a" }} />
						</IconButton>
					</Tooltip>
					<Tooltip title="Gerar pagamento">
						<IconButton size="small" onClick={() => setPaymentLinkOpen(true)}>
							<AttachMoneyIcon fontSize="small" style={{ color: "#2e7d32" }} />
						</IconButton>
					</Tooltip>
					</div>
					<div className={classes.primaryActions}>
						<ButtonWithSpinner
							loading={loading}
							startIcon={<Replay />}
							size="small"
							onClick={e => handleUpdateTicketStatus(e, "pending", null)}
						>
							{i18n.t("messagesList.header.buttons.return")}
						</ButtonWithSpinner>
						<ButtonWithSpinner
							loading={loading}
							size="small"
							variant="contained"
							color="primary"
							onClick={() => setResolveOpen(true)}
						>
							{i18n.t("messagesList.header.buttons.resolve")}
						</ButtonWithSpinner>
						<IconButton size="small" onClick={handleOpenTicketOptionsMenu}>
							<MoreVert />
						</IconButton>
					</div>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
					<ResolveTicketDialog
						open={resolveOpen}
						onClose={() => setResolveOpen(false)}
						onConfirm={handleResolveConfirm}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					color="primary"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}
			<ConversationSummaryModal
				open={summaryOpen}
				onClose={() => setSummaryOpen(false)}
				ticketId={ticket?.id}
			/>
			<AppointmentModal
				open={appointmentOpen}
				onClose={() => setAppointmentOpen(false)}
				defaultKind={appointmentKind}
				defaultTitle={
					contactName
						? `${kindDefaults[appointmentKind]?.titlePrefix || ""} — ${contactName}`
						: ""
				}
				defaultTicketId={ticket?.id}
				defaultClientId={ticket?.clientId || null}
				onSaved={() => setAppointmentOpen(false)}
			/>
			<ScheduledMessagesListModal
				open={scheduledListOpen}
				onClose={() => setScheduledListOpen(false)}
				ticketId={ticket?.id}
				contactId={ticket?.contactId}
			/>
			<SalesAiModal
				open={salesAiOpen}
				onClose={() => setSalesAiOpen(false)}
				ticketId={ticket?.id}
			/>
			<PaymentLinkModal
				open={paymentLinkOpen}
				onClose={() => setPaymentLinkOpen(false)}
				ticketId={ticket?.id}
			/>
		</div>
	);
};

export default TicketActionButtons;
